import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from 'react-toastify';
import EventBus from "../common/EventBus";
import { Modal, Button, Form, Table, InputGroup, FormControl, ProgressBar } from 'react-bootstrap';
import AdminService from "../services/admin.service";

const PKGroupManager = () => {
    const { user: currentUser } = useSelector((state) => state.auth);
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submittingAction, setSubmittingAction] = useState(null); // e.g., 'save', 'delete-1', 'add-client1'
    
    // Group Creation/Edit State
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [groupName, setGroupName] = useState("");
    const [groupDesc, setGroupDesc] = useState("");

    // Member Management State
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [showMembersModal, setShowMembersModal] = useState(false);
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);

    // Delete Confirmation State
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);

    // Group Types State
    const [allGroupTypes, setAllGroupTypes] = useState([]);
    const [selectedTypeIds, setSelectedTypeIds] = useState([]);
    const [typesLoading, setTypesLoading] = useState(false);

    // Notification Management State
    const [showNotifModal, setShowNotifModal] = useState(false);
    const [groupNotifs, setGroupNotifs] = useState([]);
    const [allNotifs, setAllNotifs] = useState([]);
    const [notifsLoading, setNotifsLoading] = useState(false);
    const [notificationEmails, setNotificationEmails] = useState({}); // { notificationId: emailString }

    useEffect(() => {
        if (currentUser && currentUser.roles.includes("ROLE_ADMIN")) {
            fetchGroups();
            fetchAllNotifications();
            fetchAllGroupTypes();
        }
    }, [currentUser]);

    const fetchAllGroupTypes = () => {
        AdminService.getAllGroupTypes().then(
            (response) => setAllGroupTypes(response.data),
            (error) => toast.error("Failed to fetch group types: " + (error.response?.data || error.message))
        );
    };

    const fetchAllNotifications = () => {
        AdminService.getAllNotifications().then(
            (response) => setAllNotifs(response.data),
            (error) => toast.error("Failed to fetch all notifications: " + (error.response?.data || error.message))
        );
    };

    const fetchGroups = () => {
        setLoading(true);
        AdminService.getClientGroups().then(
            (response) => {
                setGroups(response.data);
                setLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to fetch groups: " + (error.response?.data || error.message) });
                setLoading(false);
            }
        );
    };

    const handleOpenCreateModal = () => {
        setIsEditMode(false);
        setSelectedGroup(null);
        setGroupName("");
        setGroupDesc("");
        setSelectedTypeIds([]);
        setShowGroupModal(true);
    };

    const handleOpenEditModal = (group) => {
        setIsEditMode(true);
        setSelectedGroup(group);
        setGroupName(group.group_name);
        setGroupDesc(group.description);
        
        // Use type_ids from the group object (pre-fetched via GROUP_CONCAT)
        if (group.type_ids) {
            // Check if it's a string (from API) or array (from local update)
            const ids = typeof group.type_ids === 'string' 
                ? group.type_ids.split(',').map(id => parseInt(id))
                : group.type_ids;
            setSelectedTypeIds(ids);
        } else {
            setSelectedTypeIds([]);
        }
        
        setShowGroupModal(true);
    };

    const handleSubmitGroup = (e) => {
        e.preventDefault();
        if (!groupName.trim()) {
            toast.warning("Group name is required");
            return;
        }

        setSubmittingAction('save-group');

        const action = isEditMode
            ? AdminService.updateClientGroup(selectedGroup.group_id, groupName, groupDesc, selectedTypeIds)
            : AdminService.createClientGroup(groupName, groupDesc, selectedTypeIds);

        action.then(
            (response) => {
                toast.success(`Group ${isEditMode ? 'updated' : 'created'} successfully`);
                setShowGroupModal(false);
                
                // Get the type names for local state update
                const selectedTypeNames = allGroupTypes
                    .filter(t => selectedTypeIds.includes(t.id))
                    .map(t => t.type_name)
                    .join(',');

                if (isEditMode) {
                    setGroups(prev => prev.map(g => 
                        g.group_id === selectedGroup.group_id 
                            ? { ...g, group_name: groupName, description: groupDesc, types: selectedTypeNames, type_ids: selectedTypeIds } 
                            : g
                    ));
                } else {
                    fetchGroups(); // Easier for create to get the new ID and types correctly
                }
                setSubmittingAction(null);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: `Failed to ${isEditMode ? 'update' : 'create'} group: ` + (error.response?.data || error.message) });
                setSubmittingAction(null);
            }
        );
    };

    const handleDeleteClick = (group) => {
        setGroupToDelete(group);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = () => {
        if (!groupToDelete) return;
        
        setSubmittingAction(`delete-${groupToDelete.group_id}`);
        AdminService.deleteClientGroup(groupToDelete.group_id).then(
            () => {
                toast.success("Group deleted");
                setGroups(prev => prev.filter(g => g.group_id !== groupToDelete.group_id));
                setSubmittingAction(null);
                setShowDeleteModal(false);
                setGroupToDelete(null);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to delete group: " + (error.response?.data || error.message) });
                setSubmittingAction(null);
                setShowDeleteModal(false);
            }
        );
    };

    const openMembersModal = (group) => {
        setSelectedGroup(group);
        setMembers([]);
        setSearchQuery("");
        setSearchResults([]);
        setShowMembersModal(true);
        fetchMembers(group.group_id);
    };

    const fetchMembers = (groupId) => {
        setMembersLoading(true);
        AdminService.getGroupMembers(groupId).then(
            (response) => {
                setMembers(response.data);
                setMembersLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to fetch members: " + (error.response?.data || error.message) });
                setMembersLoading(false);
            }
        );
    };

    const handleSearch = () => {
        if (searchQuery.length < 2) return;
        setSearchLoading(true);
        AdminService.searchClients(searchQuery).then(
            (response) => {
                setSearchResults(response.data);
                setSearchLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Search failed: " + (error.response?.data || error.message) });
                setSearchLoading(false);
            }
        );
    };

    const addMember = (clientId) => {
        setSubmittingAction(`add-${clientId}`);
        AdminService.addGroupMember(selectedGroup.group_id, clientId).then(
            () => {
                toast.success("Client added to group");
                const clientToAdd = searchResults.find(r => r.oid === clientId);
                if (clientToAdd) {
                    setMembers(prev => [...prev, clientToAdd]);
                }
                setSubmittingAction(null);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to add member: " + (error.response?.data || error.message) });
                setSubmittingAction(null);
            }
        );
    };

    const removeMember = (clientId) => {
        setSubmittingAction(`remove-${clientId}`);
        AdminService.removeGroupMember(selectedGroup.group_id, clientId).then(
            () => {
                toast.success("Client removed from group");
                setMembers(prev => prev.filter(m => m.oid !== clientId));
                setSubmittingAction(null);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to remove member: " + (error.response?.data || error.message) });
                setSubmittingAction(null);
            }
        );
    };

    const openNotifModal = (group) => {
        setSelectedGroup(group);
        setGroupNotifs([]);
        setShowNotifModal(true);
        setNotifsLoading(true);
        AdminService.getGroupNotifications(group.group_id).then(
            (response) => {
                setGroupNotifs(response.data);
                setNotifsLoading(false);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to fetch group notifications: " + (error.response?.data || error.message) });
                setNotifsLoading(false);
            }
        );
    };

    const addNotification = (notifId) => {
        const email = notificationEmails[notifId] || "";
        if (!email.trim()) {
            EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Please provide at least one email address." });
            return;
        }
        setSubmittingAction(`add-notif-${notifId}`);
        AdminService.addGroupNotification(selectedGroup.group_id, notifId, email).then(
            () => {
                toast.success("Notification added to group");
                const notifToAdd = allNotifs.find(n => n.id === notifId);
                if (notifToAdd) {
                    setGroupNotifs(prev => [...prev, { ...notifToAdd, email }]);
                }
                setNotificationEmails(prev => ({ ...prev, [notifId]: "" }));
                setSubmittingAction(null);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to add notification: " + (error.response?.data || error.message) });
                setSubmittingAction(null);
            }
        );
    };

    const removeNotification = (notifId) => {
        setSubmittingAction(`remove-notif-${notifId}`);
        AdminService.removeGroupNotification(selectedGroup.group_id, notifId).then(
            () => {
                toast.success("Notification removed from group");
                setGroupNotifs(prev => prev.filter(n => n.id !== notifId));
                setSubmittingAction(null);
            },
            (error) => {
                EventBus.dispatch("SHOW_ERROR_MODAL", { title: "Error", message: "Failed to remove notification: " + (error.response?.data || error.message) });
                setSubmittingAction(null);
            }
        );
    };

    if (!currentUser) {
        return <Navigate to="/login" />;
    }

    if (!currentUser.roles.includes("ROLE_ADMIN")) {
        return <div className="container"><h3>Unauthorized</h3></div>;
    }

    return (
        <div className="container-fluid py-4" style={{ background: 'var(--bg-gradient)', minHeight: 'calc(100vh - 76px)' }}>
            <div className="card border-0 shadow-lg rounded-4 overflow-hidden mb-4">
                <div className="card-header bg-white border-0 py-3 px-4 d-flex align-items-center justify-content-between">
                    <h5 className="mb-0 fw-bold text-dark d-flex align-items-center">
                        <i className="bi bi-people-fill text-primary me-2 fs-4"></i>
                        PK Group Management
                    </h5>
                    <Button variant="primary" className="btn-premium rounded-pill px-4 shadow-sm" onClick={handleOpenCreateModal} disabled={!!submittingAction}>
                        <i className="bi bi-plus-circle me-2"></i> Create New Group
                    </Button>
                </div>

                <div className="card-body p-0">
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th className="text-secondary fw-semibold py-3 ps-4">Group Name</th>
                                    <th className="text-secondary fw-semibold py-3">Types</th>
                                    <th className="text-secondary fw-semibold py-3">Description</th>
                                    <th className="text-secondary fw-semibold py-3">Created At</th>
                                    <th className="text-secondary fw-semibold py-3 pe-4 text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="border-top-0">
                    {loading ? (
                        <tr><td colSpan="4" className="text-center"><div className="spinner-border spinner-border-sm me-2" role="status"></div>Loading groups...</td></tr>
                    ) : groups.length === 0 ? (
                        <tr><td colSpan="4" className="text-center">No groups found</td></tr>
                    ) : (
                        groups.map(g => (
                            <tr key={g.group_id}>
                                <td className="ps-4 fw-medium text-dark">{g.group_name}</td>
                                <td>
                                    {g.types && g.types.split(',').map(type => (
                                        <span key={type} className="badge bg-primary bg-opacity-10 text-primary rounded-pill me-1 fw-medium">{type}</span>
                                    ))}
                                </td>
                                <td className="text-muted small">{g.description}</td>
                                <td className="text-muted small">{new Date(g.created_at).toLocaleString()}</td>
                                <td className="pe-4">
                                    <div className="d-flex justify-content-end align-items-center gap-2 flex-nowrap">
                                        <Button 
                                            variant="light" 
                                            size="sm" 
                                            className="text-primary fw-medium rounded-pill px-3 shadow-sm" 
                                            onClick={() => handleOpenEditModal(g)}
                                            disabled={!!submittingAction}
                                        >
                                            <i className="bi bi-pencil-square me-1"></i> Edit
                                        </Button>
                                        <Button 
                                            variant="light" 
                                            size="sm" 
                                            className="text-info fw-medium rounded-pill px-3 shadow-sm" 
                                            onClick={() => openMembersModal(g)}
                                            disabled={!!submittingAction}
                                        >
                                            <i className="bi bi-people me-1"></i> Members
                                        </Button>
                                        {g.types && g.types.split(',').includes('Notification') && (
                                            <Button 
                                                variant="light" 
                                                size="sm" 
                                                className="text-success fw-medium rounded-pill px-3 shadow-sm" 
                                                onClick={() => openNotifModal(g)}
                                                disabled={!!submittingAction}
                                            >
                                                <i className="bi bi-bell me-1"></i> Notification
                                            </Button>
                                        )}
                                        <Button 
                                            variant="light" 
                                            size="sm" 
                                            className="text-danger fw-medium rounded-pill px-3 shadow-sm"
                                            onClick={() => handleDeleteClick(g)}
                                            disabled={submittingAction === `delete-${g.group_id}`}
                                        >
                                            {submittingAction === `delete-${g.group_id}` ? (
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                            ) : (
                                                <i className="bi bi-trash me-1"></i>
                                            )}
                                            Delete
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
            </Table>
            </div>
        </div>
    </div>

            {/* Create/Edit Group Modal */}
            <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)} contentClassName="border-0 shadow-lg rounded-4">
                <Modal.Header closeButton className="bg-light border-0 pb-0">
                    <Modal.Title className="fw-bold text-dark">{isEditMode ? 'Edit' : 'Create'} PK Group</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmitGroup}>
                    <Modal.Body className="pt-3 px-4">
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small fw-bold">Group Name</Form.Label>
                            <Form.Control 
                                type="text" 
                                value={groupName} 
                                onChange={(e) => setGroupName(e.target.value)}
                                placeholder="Enter group name"
                                className="form-control-lg bg-light border-0 fs-6"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small fw-bold">Description</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={3} 
                                value={groupDesc} 
                                onChange={(e) => setGroupDesc(e.target.value)}
                                placeholder="Optional description"
                                className="bg-light border-0"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="text-muted small fw-bold">Group Types</Form.Label>
                            <div className="d-flex flex-wrap bg-light rounded-3 p-3 border-0" style={{ minHeight: '50px' }}>
                                {typesLoading ? (
                                    <div className="w-100 text-center py-2">
                                        <span className="spinner-border spinner-border-sm text-primary" role="status"></span>
                                        <span className="ms-2">Loading types...</span>
                                    </div>
                                ) : allGroupTypes.map(t => (
                                    <Form.Check 
                                        key={t.id}
                                        type="checkbox"
                                        label={t.type_name}
                                        id={`type-${t.id}`}
                                        className="me-3 mb-2"
                                        checked={selectedTypeIds.includes(t.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedTypeIds(prev => [...prev, t.id]);
                                            } else {
                                                setSelectedTypeIds(prev => prev.filter(id => id !== t.id));
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 px-4 pb-4 pt-0">
                        <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={() => setShowGroupModal(false)} disabled={submittingAction === 'save-group'}>
                            Cancel
                        </Button>
                        <Button variant="primary" className="btn-premium rounded-pill px-4" type="submit" disabled={submittingAction === 'save-group'}>
                            {submittingAction === 'save-group' && (
                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            )}
                            {isEditMode ? 'Save Changes' : 'Create Group'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Members Management Modal */}
            <Modal show={showMembersModal} onHide={() => setShowMembersModal(false)} size="lg" contentClassName="border-0 shadow-lg rounded-4">
                <Modal.Header closeButton className="bg-light border-0 pb-3">
                    <Modal.Title className="fw-bold text-dark">
                        <i className="bi bi-people-fill text-info me-2"></i>
                        Manage Members: {selectedGroup?.group_name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div className="row g-4">
                        <div className="col-md-6 border-end">
                            <h6 className="fw-bold text-muted mb-3">Current Members</h6>
                            <div className="border rounded-3 p-0" style={{maxHeight: '400px', overflowY: 'auto'}}>
                                <Table size="sm" hover className="mb-0">
                                    <tbody className="border-top-0">
                                        {membersLoading ? (
                                            <tr><td className="p-3 text-center text-muted"><div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>Loading...</td></tr>
                                        ) : members.length === 0 ? (
                                            <tr><td className="p-3 text-center text-muted">No members yet</td></tr>
                                        ) : (
                                            members.map(m => (
                                                <tr key={m.oid} className="align-middle">
                                                    <td className="ps-3 fw-medium">{m.client_name}</td>
                                                    <td className="text-end pe-3">
                                                        <Button 
                                                            variant="light" 
                                                            size="sm" 
                                                            className="text-danger rounded-pill shadow-sm"
                                                            onClick={() => removeMember(m.oid)}
                                                            disabled={submittingAction === `remove-${m.oid}`}
                                                        >
                                                            {submittingAction === `remove-${m.oid}` ? (
                                                                <span className="spinner-border spinner-border-sm" role="status"></span>
                                                            ) : (
                                                                <i className="bi bi-dash-circle"></i>
                                                            )}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <h6 className="fw-bold text-muted mb-3">Add Clients</h6>
                            <InputGroup className="mb-3 shadow-sm rounded-pill overflow-hidden">
                                <FormControl
                                    className="border-0 bg-light px-4"
                                    placeholder="Search by name or ID"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                                <Button variant="primary" className="px-4" onClick={handleSearch}>
                                    <i className="bi bi-search"></i>
                                </Button>
                            </InputGroup>
                            <div className="border rounded-3 p-0" style={{maxHeight: '330px', overflowY: 'auto'}}>
                                <Table size="sm" hover className="mb-0">
                                    <tbody className="border-top-0">
                                        {searchLoading ? (
                                            <tr><td className="p-3 text-center text-muted"><div className="spinner-border spinner-border-sm text-primary me-2" role="status"></div>Searching...</td></tr>
                                        ) : searchResults.length === 0 && searchQuery ? (
                                            <tr><td className="p-3 text-center text-muted">No results</td></tr>
                                        ) : searchResults.map(r => (
                                            <tr key={r.oid} className="align-middle">
                                                <td className="ps-3 fw-medium">{r.client_name}</td>
                                                <td className="text-end pe-3">
                                                    <Button 
                                                        variant="light" 
                                                        size="sm" 
                                                        className={`rounded-pill shadow-sm ${members.some(m => m.oid === r.oid) ? 'text-secondary' : 'text-success'}`}
                                                        onClick={() => addMember(r.oid)}
                                                        disabled={members.some(m => m.oid === r.oid) || submittingAction === `add-${r.oid}`}
                                                    >
                                                        {submittingAction === `add-${r.oid}` ? (
                                                            <span className="spinner-border spinner-border-sm" role="status"></span>
                                                        ) : (
                                                            members.some(m => m.oid === r.oid) ? <i className="bi bi-check-circle-fill"></i> : <i className="bi bi-plus-circle"></i>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 px-4 pb-4 pt-0">
                    <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={() => setShowMembersModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Group Notifications Modal */}
            <Modal 
                show={showNotifModal} 
                onHide={() => setShowNotifModal(false)} 
                size="xl"
                dialogClassName="resizable-modal"
                contentClassName="border-0 shadow-lg rounded-4"
            >
                <Modal.Header closeButton className="bg-light border-0 pb-3">
                    <Modal.Title className="fw-bold text-dark">
                        <i className="bi bi-bell-fill text-success me-2"></i>
                        Manage Notifications - {selectedGroup?.group_name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <div className="row g-4">
                        <div className="col-md-5 border-end">
                            <h6 className="fw-bold text-muted mb-3">Assigned Notifications</h6>
                            <div className="table-responsive border rounded-3 p-0" style={{ maxHeight: '400px' }}>
                                <Table size="sm" hover className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="ps-3 py-2 text-secondary fw-semibold">Notification Name</th>
                                            <th className="py-2 text-secondary fw-semibold">Emails</th>
                                            <th className="text-end pe-3 py-2 text-secondary fw-semibold">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="border-top-0">
                                        {notifsLoading ? (
                                            <tr><td colSpan="3" className="p-4 text-center text-muted"><div className="spinner-border spinner-border-sm text-primary me-2"></div>Loading...</td></tr>
                                        ) : groupNotifs.length === 0 ? (
                                            <tr><td colSpan="3" className="p-4 text-center text-muted">No notifications assigned</td></tr>
                                        ) : (
                                            groupNotifs.map(n => (
                                                <tr key={n.id} className="align-middle">
                                                    <td className="ps-3 fw-medium">{n.name}</td>
                                                    <td>
                                                        <small className="text-muted text-break">{n.email || "No email defined"}</small>
                                                    </td>
                                                    <td className="text-end pe-3">
                                                        <Button 
                                                            variant="light" 
                                                            size="sm"
                                                            className="text-danger rounded-pill shadow-sm px-3" 
                                                            onClick={() => removeNotification(n.id)}
                                                            disabled={submittingAction === `remove-notif-${n.id}`}
                                                        >
                                                            {submittingAction === `remove-notif-${n.id}` ? (
                                                                <span className="spinner-border spinner-border-sm"></span>
                                                            ) : "Remove"}
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                        <div className="col-md-7">
                            <h6 className="fw-bold text-muted mb-3">Available Notifications</h6>
                            <div className="table-responsive border rounded-3 p-0" style={{ maxHeight: '400px' }}>
                                <Table size="sm" hover className="mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th className="ps-3 py-2 text-secondary fw-semibold">Notification Name</th>
                                            <th className="py-2 text-secondary fw-semibold">Target Emails (separated by ;)</th>
                                            <th className="text-end pe-3 py-2 text-secondary fw-semibold">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="border-top-0">
                                        {allNotifs.map(n => (
                                            <tr key={n.id} className="align-middle">
                                                <td className="ps-3 fw-medium">{n.name}</td>
                                                <td>
                                                    <Form.Control 
                                                        size="sm"
                                                        type="text"
                                                        className="bg-light border-0"
                                                        placeholder="email1@test.com;email2@test.com"
                                                        value={notificationEmails[n.id] || ""}
                                                        onChange={(e) => setNotificationEmails(prev => ({ ...prev, [n.id]: e.target.value }))}
                                                        disabled={groupNotifs.some(gn => gn.id === n.id) || submittingAction === `add-notif-${n.id}`}
                                                    />
                                                </td>
                                                <td className="text-end pe-3">
                                                    <Button 
                                                        variant="light" 
                                                        size="sm" 
                                                        className={`rounded-pill shadow-sm px-3 fw-medium ${groupNotifs.some(gn => gn.id === n.id) ? 'text-secondary' : 'text-success'}`}
                                                        onClick={() => addNotification(n.id)}
                                                        disabled={groupNotifs.some(gn => gn.id === n.id) || submittingAction === `add-notif-${n.id}`}
                                                    >
                                                        {submittingAction === `add-notif-${n.id}` && (
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                        )}
                                                        {groupNotifs.some(gn => gn.id === n.id) ? "Added" : "Add"}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 px-4 pb-4 pt-0">
                    <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={() => setShowNotifModal(false)}>Close</Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => !submittingAction && setShowDeleteModal(false)} centered contentClassName="border-0 shadow-lg rounded-4">
                <Modal.Header closeButton={!submittingAction} className="bg-danger bg-opacity-10 border-0 pb-3">
                    <Modal.Title className="fw-bold text-danger">
                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                        Confirm Delete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4 text-center">
                    <p className="mb-2 fs-5">Are you sure you want to delete the group <strong>{groupToDelete?.group_name}</strong>?</p>
                    <p className="text-danger small fw-bold mb-0">This will remove all associated members. This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer className="border-0 px-4 pb-4 pt-0 justify-content-center">
                    <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={() => setShowDeleteModal(false)} disabled={!!submittingAction}>
                        Cancel
                    </Button>
                    <Button variant="danger" className="rounded-pill px-4" onClick={handleConfirmDelete} disabled={!!submittingAction}>
                        {submittingAction?.startsWith('delete-') && (
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        )}
                        Delete Group
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PKGroupManager;
